// RewardVerification.js - Page for merchants to verify reward redemptions
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRedemptionDetails } from '../api/rewards';
import './RewardVerification.css';

const RewardVerification = () => {
  const { redemptionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [redemption, setRedemption] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRedemptionDetails();
  }, [redemptionId]);

  const fetchRedemptionDetails = async () => {
    try {
      setLoading(true);
      const data = await getRedemptionDetails(redemptionId);
      setRedemption(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching redemption:', err);
      setError(err.message || 'Invalid or expired redemption code');
    } finally {
      setLoading(false);
    }
  };

  const isValid = () => {
    if (!redemption) return false;
    
    // Only check expiration (24 hours from redemption)
    // Removed used check to allow unlimited redemptions
    const redemptionTime = redemption.redeemedAt?.toDate?.() || new Date(redemption.redeemedAt);
    const expirationTime = new Date(redemptionTime.getTime() + (24 * 60 * 60 * 1000));
    const now = new Date();
    
    return now < expirationTime;
  };

  const getTimeRemaining = () => {
    if (!redemption?.redeemedAt) return '';
    
    const redemptionTime = redemption.redeemedAt?.toDate?.() || new Date(redemption.redeemedAt);
    const expirationTime = new Date(redemptionTime.getTime() + (24 * 60 * 60 * 1000));
    const now = new Date();
    const remainingMs = expirationTime - now;
    
    if (remainingMs <= 0) return 'Expired';
    
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m remaining`;
  };

  if (loading) {
    return (
      <div className="verification-page">
        <div className="verification-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Verifying redemption...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verification-page">
        <div className="verification-container">
          <div className="verification-card error">
            <div className="verification-icon error-icon">❌</div>
            <h1>Invalid Redemption</h1>
            <p className="error-message">{error}</p>
            <div className="verification-details">
              <p>This QR code may be invalid or expired.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const valid = isValid();
  const timeRemaining = getTimeRemaining();

  return (
    <div className="verification-page">
      <div className="verification-container">
        <div className={`verification-card ${valid ? 'valid' : 'invalid'}`}>
          {/* Status Icon */}
          <div className={`verification-icon ${valid ? 'valid-icon' : 'invalid-icon'}`}>
            {valid ? '✓' : '✗'}
          </div>

          {/* Status Title */}
          <h1>{valid ? 'Valid Redemption' : 'Invalid Redemption'}</h1>

          {!valid && (
            <p className="status-message">This redemption has expired</p>
          )}

          {/* Reward Details */}
          <div className="verification-details">
            <div className="detail-section">
              <h2>Reward Details</h2>
              <div className="detail-row">
                <span className="detail-label">Merchant:</span>
                <span className="detail-value">{redemption.reward?.merchantName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Offer:</span>
                <span className="detail-value">{redemption.reward?.title}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Discount:</span>
                <span className="detail-value discount-badge">{redemption.reward?.discount}</span>
              </div>
              {redemption.reward?.terms && (
                <div className="detail-row terms">
                  <span className="detail-label">Terms:</span>
                  <span className="detail-value">{redemption.reward?.terms}</span>
                </div>
              )}
            </div>

            {/* Member Details */}
            <div className="detail-section">
              <h2>Member Information</h2>
              <div className="detail-row">
                <span className="detail-label">Name:</span>
                <span className="detail-value">
                  {redemption.user?.title} {redemption.user?.firstName} {redemption.user?.lastName}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Membership ID:</span>
                <span className="detail-value">{redemption.user?.membershipID || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Tier:</span>
                <span className="detail-value tier-badge">Tier {redemption.user?.tier || 1}</span>
              </div>
            </div>

            {/* Redemption Details */}
            <div className="detail-section">
              <h2>Redemption Information</h2>
              <div className="detail-row">
                <span className="detail-label">Redeemed:</span>
                <span className="detail-value">
                  {redemption.redeemedAt?.toDate?.().toLocaleString() || 
                   new Date(redemption.redeemedAt).toLocaleString()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status-badge ${valid ? 'valid' : 'invalid'}`}>
                  {valid ? 'Valid' : 'Expired'}
                </span>
              </div>
              {valid && (
                <div className="detail-row">
                  <span className="detail-label">Validity:</span>
                  <span className="detail-value validity-timer">{timeRemaining}</span>
                </div>
              )}
            </div>
          </div>

          {/* Instructions for Merchant */}
          {valid && (
            <div className="merchant-instructions">
              <h3>Instructions for Merchant:</h3>
              <ol>
                <li>Verify the member's identity matches the information above</li>
                <li>Apply the discount: <strong>{redemption.reward?.discount}</strong></li>
                <li>Member can redeem this offer multiple times</li>
                <li>Valid for the next {timeRemaining}</li>
              </ol>
            </div>
          )}

          {/* Redemption ID */}
          <div className="redemption-id">
            <small>Redemption ID: {redemptionId}</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardVerification;
